// src/components/NoticiaForm.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { NoticiaCreate, Autor } from '../../types';
import { api } from '../../api/client';
import RichTextEditor from '../shared/RichTextEditor';

interface Props {
  initialData?: NoticiaCreate;
  onSubmit: () => void;
  onCancel: () => void;
  isEdit?: boolean;
  id?: number;
}

export default function NoticiaForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  id,
}: Props) {
  const [autores, setAutores] = useState<Autor[]>([]);
  const [imagen, setImagen] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NoticiaCreate>(
    initialData || {
      titulo: '',
      resumen: '',
      contenido: '',
      url_imagen: '',
      video_url: '',
      fecha_publicacion: new Date().toISOString().slice(0, 16),
      id_autor: 0,
      categoria: '',
      imagen: ''
    }
  );

  useEffect(() => {
    api.autores.list().then(setAutores);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Construir FormData para enviar al backend (igual que ProductoForm)
      const formData = new FormData();
      formData.append('titulo', data.titulo);
      formData.append('resumen', data.resumen || '');
      formData.append('contenido', data.contenido || '');
      formData.append('fecha_publicacion', data.fecha_publicacion);
      formData.append('id_autor', data.id_autor.toString());
      formData.append('categoria', data.categoria || '');
      if (data.video_url) formData.append('video_url', data.video_url);
      if (imagen) formData.append('imagen', imagen);
      if (isEdit && id) {
        await api.noticias.update(id, formData);
      } else {
        await api.noticias.create(formData);
      }
      onSubmit();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al guardar la noticia';
      setError(errorMessage);
      console.error('Error al enviar noticia:', err);
    } finally {
      setLoading(false);
    }
  }, [data, imagen, isEdit, id, onSubmit]);

  return (
    <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow mb-4">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}
      <div className="space-y-4">
        {/* Título: texto plano */}
        <div>
          <label className="block mb-1 font-medium">Título *</label>
          <input
            type="text"
            className="border p-2 w-full rounded"
            value={data.titulo}
            onChange={(e) => setData({ ...data, titulo: e.target.value })}
            required
            placeholder="Escribe el título de la noticia"
            disabled={loading}
          />
        </div>
        {/* Resumen: editor rico */}
        <div>
          <label className="block mb-1 font-medium">Resumen</label>
          <RichTextEditor
            value={data.resumen || ''}
            onChange={(html) => setData({ ...data, resumen: html })}
            placeholder="Resumen breve de la noticia..."
            minHeight="150px"
          />
        </div>
        {/* Contenido: editor rico */}
        <div>
          <label className="block mb-1 font-medium">Contenido *</label>
          <RichTextEditor
            value={data.contenido || ''}
            onChange={(html) => setData({ ...data, contenido: html })}
            placeholder="Contenido completo de la noticia..."
            minHeight="250px"
          />
        </div>
        {/* Fecha */}
        <div>
          <label className="block mb-1 font-medium">Fecha de publicación *</label>
          <input
            type="datetime-local"
            className="border p-2 w-full rounded"
            value={data.fecha_publicacion.slice(0, 16)}
            onChange={(e) =>
              setData({ ...data, fecha_publicacion: e.target.value + ':00' })
            }
            required
            disabled={loading}
          />
        </div>
        {/* Autor */}
        <div>
          <label className="block mb-1 font-medium">Autor *</label>
          <select
            className="border p-2 w-full rounded"
            value={data.id_autor || ''}
            onChange={(e) =>
              setData({ ...data, id_autor: parseInt(e.target.value) })
            }
            required
            disabled={loading}
          >
            <option value="">Selecciona un autor</option>
            {autores.map((a) => (
              <option key={a.id_autor} value={a.id_autor}>
                {a.nombre_autor}
              </option>
            ))}
          </select>
        </div>

        {/* Categoría */}
        <div>
          <label className="block mb-1 font-medium">Categoría</label>
          <input
            className="border p-2 w-full rounded"
            placeholder="Categoría"
            value={data.categoria || ''}
            onChange={(e) => setData({ ...data, categoria: e.target.value })}
            disabled={loading}
          />
        </div>

        {/* Video URL */}
        <div>
          <label className="block mb-1 font-medium">URL del Video (opcional)</label>
          <input
            type="url"
            className="border p-2 w-full rounded"
            placeholder="https://www.youtube.com/watch?v=..."
            value={data.video_url || ''}
            onChange={(e) => setData({ ...data, video_url: e.target.value })}
            disabled={loading}
          />
          <p className="text-sm text-gray-500 mt-1">
            Ingresa la URL de un video de YouTube, Vimeo u otra plataforma
          </p>
        </div>

        {/* Imagen */}
        <div>
          <label className="block mb-1 font-medium">Imagen</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImagen(e.target.files?.[0] || null)}
            className="w-full"
            disabled={loading}
          />
          {data.url_imagen && (
            <img
              src={data.url_imagen}
              alt="Vista previa"
              className="mt-2 max-h-40 border rounded"
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex space-x-2">
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Publicar'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:bg-gray-300"
          disabled={loading}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}